import { forEachEventInput, forEachSourceStep, hasStartEventInput, previousOutputInputs } from '../../shared/draft-graph-traversal';
import { StepActionTypeSchema } from '../../shared/step-action-type.schema';
function taskOf(step) {
    return step.task;
}
function rulesOf(step) {
    return step.rules;
}
function isBlank(value) {
    return !value || value.trim() === '';
}
/**
 * Validates per-node content at publish time.
 *
 * Steps must have:
 * - actionType
 * - assignedTo + assignedToModel
 * - task.taskDescription (if actionType === 'task')
 * - at least one rule (if actionType === 'decision')
 * - each decision rule must have a non-blank `output` and (when the decision
 *   declares any condition columns at all) at least one filled condition
 *   value — matches `DecisionTableDialog.handleSave` so the pre-publish
 *   warning agrees with the final validation. The "rule must have a
 *   downstream successor or terminal status" check lives in
 *   `validateDraftGraph` as the `rule-no-output` reason.
 *
 * Assignments must have:
 * - assignedTo + assignedToModel
 * - at least one upstream edge: a previous_output input OR an event input
 *   linked to a start event node. A start-event attachment is connectivity
 *   on par with a previous_output edge (ADR 0003 P3b) — the recording
 *   kickoff flow itself creates start-anchored invites, and dispatch
 *   resolves them via `deriveAssignmentAnchor`. Non-start event inputs
 *   still don't count — they are entry points, not edges.
 *
 * Events must have:
 * - a non-blank name (start AND intermediate — no eventKind exemption). The
 *   auto-minted start row carries `name: ''` (`reconcile-draft-event-rows.ts`),
 *   and publish is what starts the recording — so the Start Recording page
 *   gates its start button on these event rules.
 * - a non-blank description (start event only).
 */ function validateDraftContent(steps, assignments, events, startEventIds) {
    const errors = [];
    for (const step of steps){
        const issues = [];
        if (!step.actionType) {
            issues.push({
                path: [
                    'actionType'
                ],
                message: 'actionType is required for steps',
                code: 'custom'
            });
        } else if (step.actionType === StepActionTypeSchema.enum.task) {
            const task = taskOf(step);
            if (isBlank(task?.taskDescription)) {
                issues.push({
                    path: [
                        'task',
                        'taskDescription'
                    ],
                    message: 'Task description is required',
                    code: 'custom'
                });
            }
            if (isBlank(task?.output)) {
                issues.push({
                    path: [
                        'task',
                        'output'
                    ],
                    message: 'Task output is required',
                    code: 'custom'
                });
            }
        } else if (step.actionType === StepActionTypeSchema.enum.decision) {
            const rules = rulesOf(step);
            if (!rules || rules.length === 0) {
                issues.push({
                    path: [
                        'rules'
                    ],
                    message: 'At least one decision rule is required',
                    code: 'too_small'
                });
            } else {
                // A rule needs a non-blank `output` and at least ONE condition column
                // filled in — sparse tables are allowed, so a row with some blank
                // cells and one filled cell still matches. Pure-branching tables (no
                // condition columns at all) are exempt from the condition check.
                // Matches `DecisionTableDialog.handleSave`'s check so the in-editor
                // warning and the publish validation agree.
                const hasAnyCondition = rules.some((rule)=>(rule.inputs ?? []).length > 0);
                rules.forEach((rule, index)=>{
                    if (isBlank(rule.output)) {
                        issues.push({
                            path: [
                                'rules',
                                index,
                                'output'
                            ],
                            message: 'Rule output is required',
                            code: 'custom'
                        });
                    }
                    if (!hasAnyCondition) return;
                    const hasFilledCondition = (rule.inputs ?? []).some(({ value })=>!isBlank(value));
                    if (!hasFilledCondition) {
                        issues.push({
                            path: [
                                'rules',
                                index,
                                'inputs'
                            ],
                            message: 'Rule must have at least one condition value filled in',
                            code: 'custom'
                        });
                    }
                });
            }
        }
        if (!step.assignedTo || !step.assignedToModel) {
            issues.push({
                path: [
                    'assignedTo'
                ],
                message: 'Steps must have an assignee',
                code: 'custom'
            });
        }
        if (issues.length > 0) {
            errors.push({
                stepId: String(step.stableStepId),
                issues
            });
        }
    }
    for (const assignment of assignments){
        const issues = [];
        if (!assignment.assignedTo || !assignment.assignedToModel) {
            issues.push({
                path: [
                    'assignedTo'
                ],
                message: 'Assignments must have an assignee',
                code: 'custom'
            });
        }
        if (previousOutputInputs(assignment.inputs).length === 0 && !hasStartEventInput(assignment.inputs, startEventIds)) {
            issues.push({
                path: [
                    'inputs'
                ],
                message: 'Assignments must have at least one previous_output input',
                code: 'custom'
            });
        }
        if (issues.length > 0) {
            errors.push({
                stepId: String(assignment.stableStepId),
                issues
            });
        }
    }
    for (const event of events){
        const issues = eventContentIssues(event);
        if (issues.length > 0) {
            errors.push({
                stepId: String(event._id),
                issues
            });
        }
    }
    return errors;
}
export function eventContentIssues(event) {
    const issues = [];
    if (isBlank(event.name)) {
        issues.push({
            path: [
                'name'
            ],
            message: 'Event name is required',
            code: 'custom'
        });
    }
    if (event.eventKind === 'start' && isBlank(event.description)) {
        issues.push({
            path: [
                'description'
            ],
            message: 'Start event description is required',
            code: 'custom'
        });
    }
    return issues;
}
function collectEdges(nodes) {
    const edges = [];
    const connectedRuleIdsByStep = new Map();
    for (const node of nodes){
        const toId = String(node.stableStepId);
        forEachSourceStep(node.inputs, (src)=>{
            if (!src.stepId) return;
            const fromId = String(src.stepId);
            edges.push({
                from: fromId,
                to: toId
            });
            for (const ruleId of src.ruleIds ?? []){
                if (!ruleId) continue;
                let set = connectedRuleIdsByStep.get(fromId);
                if (!set) {
                    set = new Set();
                    connectedRuleIdsByStep.set(fromId, set);
                }
                set.add(String(ruleId));
            }
        });
    }
    return {
        edges,
        connectedRuleIdsByStep
    };
}
/**
 * Is the node start-connected — via `previousStep`, an
 * `inputs[].sourceSteps[].stepId` start tag, or an `event` input whose
 * `eventId` names a start event? Shared with the client convert gate, which
 * opts into the canonical `eventId` arm only.
 *
 * NOT a standalone "start-anchored" predicate: its `eventId` arm overlaps
 * `hasEventInput`, and that double-count is only harmless because both callers
 * fold this and `hasEventInput` into the same `effectiveIn === 0` test. Any
 * other reader must call `hasStartEventInput` with the options it actually
 * wants.
 */ function hasStartInput(node, startEventIds) {
    return hasStartEventInput(node.inputs, startEventIds, {
        previousStep: node.previousStep,
        sourceTags: true
    });
}
// An event input without `eventId` is a legacy placeholder — it doesn't point
// at a first-class event node, so it can't act as a graph entry point. Only
// count event inputs that carry an `eventId`.
function hasEventInput(node) {
    for (const input of node.inputs ?? []){
        if (input.inputType === 'event' && input.eventId) return true;
    }
    return false;
}
function isTerminal(status) {
    return status === 'UNCLEAR' || status === 'NONE';
}
function hasTerminalSuccessor(step) {
    // Only the bucket matching `actionType` carries graph-relevant successors.
    // The draft keeps both `task` and `rules` subdocs so a decision↔task toggle
    // round-trips without losing the user's work — so a leftover terminal status
    // in the inactive bucket must not be read as an output.
    if (step.actionType !== StepActionTypeSchema.enum.decision) {
        return isTerminal(taskOf(step)?.successors?.successorStatus);
    }
    for (const rule of rulesOf(step) ?? []){
        if (isTerminal(rule.successors?.successorStatus)) return true;
    }
    return false;
}
/**
 * A decision rule is stranded iff it has an `_id`, no downstream input
 * references it through `sourceSteps[].ruleIds`, and it is not marked
 * terminal (UNCLEAR / NONE). Only decisions carry live rules: a task can keep
 * a leftover `rules[]` so a decision↔task toggle round-trips, and those are
 * never wired to a successor.
 */ function strandedRuleIds(step, connectedRuleIds) {
    const stranded = new Set();
    if (step.actionType !== StepActionTypeSchema.enum.decision) return stranded;
    for (const rule of rulesOf(step) ?? []){
        if (!rule._id) continue;
        const ruleId = String(rule._id);
        if (connectedRuleIds?.has(ruleId)) continue;
        if (isTerminal(rule.successors?.successorStatus)) continue;
        stranded.add(ruleId);
    }
    return stranded;
}
/**
 * Decision step id → its stranded rule ids, whatever the step's degree — the
 * editor's per-handle hint. The publish gate reports only the stranded rules
 * of decisions that passed the step-level degree checks.
 */ export function strandedRuleIdsByStep(steps, assignments) {
    const { connectedRuleIdsByStep } = collectEdges([
        ...steps,
        ...assignments
    ]);
    const result = new Map();
    for (const step of steps){
        const id = String(step.stableStepId);
        const stranded = strandedRuleIds(step, connectedRuleIdsByStep.get(id));
        if (stranded.size > 0) result.set(id, stranded);
    }
    return result;
}
/**
 * Graph-level publish validation.
 *
 * - Every draft must carry at least one start event node (`eventKind:
 *   'start'`). The row is auto-minted on every draft read since issue 07, so
 *   a missing one guards corruption, not normal flow.
 * - Orphan nodes (0 in + 0 out) are invalid.
 * - Nodes with 0 inputs are invalid unless they are start-connected (any
 *   `previousStep`/`sourceSteps`/`eventId` start form — see `hasStartInput`)
 *   or carry an event input referencing a declared event node (`eventId`).
 * - Steps with 0 outputs are invalid unless they have a terminal successor
 *   (UNCLEAR / NONE). Draft assignments downstream of a step count as real
 *   outgoing edges via their own `inputs` (collected by `collectEdges`).
 * - Assignments never need outputs — they represent unrecorded placeholders
 *   that publish will convert into invitations. The assignee records their
 *   own outputs later.
 * - The whole draft (start events + steps + assignments + intermediate
 *   events) must form a single connected component. Start event nodes are
 *   entry points: they're only required to be in that component when wired
 *   (a process driven entirely by intermediate-event entries is legal), and
 *   they're exempt from the floating-event rule for the same reason.
 */ function validateDraftGraph(steps, assignments, events, startEventIds) {
    const errors = [];
    const allNodes = [
        ...steps,
        ...assignments
    ];
    const { edges, connectedRuleIdsByStep } = collectEdges(allNodes);
    if (startEventIds.size === 0) {
        errors.push({
            reason: 'no-start-event'
        });
    }
    const disconnectedEventIds = new Set();
    if (events.length > 0) {
        const referencedEventIds = new Set();
        for (const node of allNodes){
            for (const input of node.inputs ?? []){
                if (input.inputType !== 'event') continue;
                if (input.eventId) {
                    referencedEventIds.add(String(input.eventId));
                }
            }
        }
        for (const event of events){
            // Start events are entry points — an unwired one is a legal state (the
            // user hasn't connected the first step yet / the process is driven by
            // intermediate events), not a floating event.
            if (event.eventKind === 'start') continue;
            const idStr = String(event._id);
            if (!referencedEventIds.has(idStr)) {
                disconnectedEventIds.add(idStr);
                errors.push({
                    stepId: idStr,
                    reason: 'event-disconnected'
                });
            }
        }
    }
    const inDegree = new Map();
    const outDegree = new Map();
    for (const e of edges){
        outDegree.set(e.from, (outDegree.get(e.from) ?? 0) + 1);
        inDegree.set(e.to, (inDegree.get(e.to) ?? 0) + 1);
    }
    // Nodes that already have a step-level degree error (`orphan`, `no-inputs`,
    // `no-outputs`). `unreachable` is suppressed for these since the degree
    // error is a stronger signal and the UI would otherwise double-flag them.
    const degreeFlaggedNodeIds = new Set();
    for (const step of steps){
        const id = String(step.stableStepId);
        const ins = inDegree.get(id) ?? 0;
        const outs = outDegree.get(id) ?? 0;
        const startInput = hasStartInput(step, startEventIds);
        const eventInput = hasEventInput(step);
        const terminal = hasTerminalSuccessor(step);
        const effectiveIn = ins + (startInput ? 1 : 0) + (eventInput ? 1 : 0);
        const effectiveOut = outs + (terminal ? 1 : 0);
        if (effectiveIn === 0 && effectiveOut === 0) {
            errors.push({
                stepId: id,
                reason: 'orphan'
            });
            degreeFlaggedNodeIds.add(id);
            continue;
        }
        if (effectiveIn === 0) {
            errors.push({
                stepId: id,
                reason: 'no-inputs'
            });
            degreeFlaggedNodeIds.add(id);
            continue;
        }
        if (effectiveOut === 0) {
            errors.push({
                stepId: id,
                reason: 'no-outputs'
            });
            degreeFlaggedNodeIds.add(id);
            continue;
        }
        // Step-level `no-outputs` already caught the case where every rule is
        // stranded — this catches the mixed case where one rule is wired up
        // (keeping `effectiveOut > 0`) while another is not.
        for (const ruleId of strandedRuleIds(step, connectedRuleIdsByStep.get(id))){
            errors.push({
                stepId: id,
                reason: 'rule-no-output',
                ruleId
            });
        }
    }
    for (const a of assignments){
        const id = String(a.stableStepId);
        const ins = inDegree.get(id) ?? 0;
        const startInput = hasStartInput(a, startEventIds);
        const eventInput = hasEventInput(a);
        const effectiveIn = ins + (startInput ? 1 : 0) + (eventInput ? 1 : 0);
        if (effectiveIn === 0) {
            errors.push({
                stepId: id,
                reason: 'no-inputs'
            });
            degreeFlaggedNodeIds.add(id);
        }
    }
    // Connectivity: build an undirected graph over {start events, steps,
    // assignments, intermediate events} and require it to form a single
    // connected component. A start event is only required to be in the
    // component if something is wired to it — a process driven entirely by
    // event-triggered entries is legal.
    if (allNodes.length > 0 || events.length > 0) {
        const firstStartEvent = events.find((ev)=>ev.eventKind === 'start');
        // With no start row (corrupt draft — already flagged above) there is no
        // start component to seed; the fallback BFS below covers the rest.
        const primaryStartId = firstStartEvent?._id.toString();
        const adj = new Map();
        const touchNode = (id)=>{
            if (!adj.has(id)) adj.set(id, new Set());
        };
        const addEdge = (a, b)=>{
            touchNode(a);
            touchNode(b);
            adj.get(a).add(b);
            adj.get(b).add(a);
        };
        if (primaryStartId) touchNode(primaryStartId);
        for (const n of allNodes)touchNode(String(n.stableStepId));
        for (const e of events)touchNode(String(e._id));
        for (const node of allNodes){
            const nodeId = String(node.stableStepId);
            // Mirror `hasStartInput`: a start-anchored `previousStep` (start event
            // node id) attaches the step to the start component even when its
            // event input lacks an `eventId` and carries no source tag. The legacy
            // `'START'` sentinel is no longer an entry form (ADR 0003 P6).
            const prev = 'previousStep' in node ? node.previousStep : undefined;
            if (prev != null && startEventIds.has(String(prev))) addEdge(nodeId, String(prev));
            forEachSourceStep(node.inputs, (src)=>{
                if (!src.stepId) return;
                addEdge(nodeId, String(src.stepId));
            });
            forEachEventInput(node.inputs, (eventId, sourceSteps)=>{
                if (eventId) addEdge(nodeId, String(eventId));
                // Mirror `hasStartInput`: a source tag on an event input is an edge
                // from this node to that source node.
                for (const src of sourceSteps){
                    if (!src.stepId) continue;
                    addEdge(nodeId, String(src.stepId));
                }
            });
        }
        const visited = new Set();
        const bfs = (root)=>{
            const queue = [
                root
            ];
            visited.add(root);
            while(queue.length > 0){
                const cur = queue.shift();
                for (const nxt of adj.get(cur) ?? []){
                    if (visited.has(nxt)) continue;
                    visited.add(nxt);
                    queue.push(nxt);
                }
            }
        };
        const startHasEdges = primaryStartId != null && (adj.get(primaryStartId)?.size ?? 0) > 0;
        if (startHasEdges) {
            bfs(primaryStartId);
        } else {
            // No start wiring: the remaining graph must still be one component.
            // Pick the first node with any edge as the seed — isolated nodes (e.g.
            // a true orphan step) are already flagged via the degree pass, so BFS
            // from them would not recover them anyway.
            for (const [id, neighbors] of adj){
                if (id === primaryStartId) continue;
                if (neighbors.size === 0) continue;
                bfs(id);
                break;
            }
        }
        for (const node of allNodes){
            const id = String(node.stableStepId);
            if (visited.has(id)) continue;
            if (degreeFlaggedNodeIds.has(id)) continue;
            errors.push({
                stepId: id,
                reason: 'unreachable'
            });
        }
        for (const event of events){
            const id = String(event._id);
            if (visited.has(id)) continue;
            if (disconnectedEventIds.has(id)) continue;
            // An unwired start event is a legal entry-point state, not unreachable.
            if (event.eventKind === 'start' && (adj.get(id)?.size ?? 0) === 0) {
                continue;
            }
            errors.push({
                stepId: id,
                reason: 'unreachable'
            });
        }
    }
    return errors;
}
function startEventIdsOf(events) {
    return new Set(events.filter((event)=>event.eventKind === 'start').map((event)=>String(event._id)));
}
/**
 * The publish gate's per-node content rules alone, for a carrier that judges
 * the graph its own way and would only discard `graphErrors`.
 */ export function validateDraftContentForPublish(steps, assignments, events) {
    return validateDraftContent(steps, assignments, events, startEventIdsOf(events));
}
/**
 * The full publish gate: the 400 body's `contentErrors` and `graphErrors`,
 * both empty when the draft may publish.
 */ export function validateDraftForPublish(steps, assignments, events) {
    const startEventIds = startEventIdsOf(events);
    return {
        contentErrors: validateDraftContent(steps, assignments, events, startEventIds),
        graphErrors: validateDraftGraph(steps, assignments, events, startEventIds)
    };
}

//# sourceMappingURL=validate-draft-for-publish.js.map